import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, Typography, IconButton, Grid, Tabs, Tab } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { alpha } from "@mui/material/styles";
import { PromptEnhancementModalProps } from "./types";
import { apiRequest } from "../../../../utils/api";
import { GradientTypography } from "../../../../utils/gradientTypography";
import { useAlert } from "../../../../context/AlertContext";
import GeneratedImageModal from "../../../../components/common/GeneratedImageModal";
import TextInputTab from "./TextInputTab";
import ImageAnalysisTab from "./ImageAnalysisTab";
import OutputSection from "./OutputSection";

const PromptEnhancementModal: React.FC<PromptEnhancementModalProps> = ({
  open,
  onClose,
  onInsert,
  initialPrompt = "",
  aiEngine = "",
  modelVersion = "",
  mode = "image",
  promptImage = null,
}) => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inputPrompt, setInputPrompt] = useState(initialPrompt);
  const [outputPrompt, setOutputPrompt] = useState("");
  const [outputPromptNative, setOutputPromptNative] = useState("");
  const [imageDescription, setImageDescription] = useState("");
  const [imageDescriptionNative, setImageDescriptionNative] = useState("");
  const [enhanceLoading, setEnhanceLoading] = useState(false);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [generatedImageModalOpen, setGeneratedImageModalOpen] = useState(false);

  // クッキーから言語設定を取得
  const uiLanguage = Cookies.get("ui_language");
  const showTranslate = uiLanguage !== "en";

  // soundモードでは画像タブを非表示
  const showImageTab = mode !== "sound";

  useEffect(() => {
    if (open) {
      // initialPromptが文字列でない場合の安全な処理
      const promptStr = typeof initialPrompt === "string" ? initialPrompt : String(initialPrompt || "");
      setInputPrompt(promptStr);
      setOutputPrompt("");
      setOutputPromptNative("");
      setImageDescription("");
      setImageDescriptionNative("");
      setError("");
      // promptImageを初期値としてセット（モーダルが開かれた時のみ）
      setUploadedImage(promptImage || null);
      setActiveTab(0); // タブを初期化
    }
  }, [open, initialPrompt, promptImage]);

  // 相互排他的なローディング状態をチェック
  const isAnyLoading = enhanceLoading || translateLoading || analyzeLoading;

  const handleEnhance = async () => {
    const promptStr = typeof inputPrompt === "string" ? inputPrompt : String(inputPrompt || "");
    if (!promptStr.trim()) {
      setError(t("juno.enhancement.error.emptyPrompt"));
      return;
    }

    setEnhanceLoading(true);
    setError("");

    try {
      let endpoint = "/api/v1/juno/update-prompt";
      type Payload =
        | { prompt: string; aiEngine: string }
        | { prompt: string; aiEngine: string; modelVersion: string }
        | { prompt: string; soundType: string; model: string };

      let payload: Payload = { prompt: promptStr, aiEngine };

      if (mode === "video") {
        endpoint = "/api/v1/juno/update-video-prompt";
        payload = { prompt: promptStr, aiEngine, modelVersion };
      } else if (mode === "sound") {
        endpoint = "/api/v1/juno/sound/update-prompt";
        payload = { prompt: promptStr, soundType: "music", model: aiEngine };
      }

      const response = await apiRequest(endpoint, "POST", payload);

      // 全モード共通の構造化レスポンス処理
      if (response.data.prompt_en) {
        setOutputPrompt(response.data.prompt_en);
        setOutputPromptNative(response.data.prompt_native || "");
      } else {
        setError(t("juno.enhancement.error.noResponse"));
      }
    } catch (err: any) {
      console.error("Enhancement error:", err);
      setError(err.response?.data?.message || t("juno.enhancement.error.failed"));
    } finally {
      setEnhanceLoading(false);
    }
  };

  const handleTranslate = async () => {
    const promptStr = typeof inputPrompt === "string" ? inputPrompt : String(inputPrompt || "");
    if (!promptStr.trim()) {
      setError(t("juno.enhancement.error.emptyPrompt"));
      return;
    }

    setTranslateLoading(true);
    setError("");

    try {
      let endpoint = "/api/v1/juno/translate-prompt";

      if (mode === "video") {
        endpoint = "/api/v1/juno/translate-video-prompt";
      }
      // soundモードでは翻訳機能は使用しない（showTranslateで制御済み）

      const response = await apiRequest(endpoint, "POST", { prompt: promptStr });

      if (response.data.prompt_en) {
        setOutputPrompt(response.data.prompt_en);
        setOutputPromptNative(response.data.prompt_native || "");
      } else {
        setError(t("juno.enhancement.error.noResponse"));
      }
    } catch (err: any) {
      console.error("Translation error:", err);
      setError(err.response?.data?.message || t("juno.enhancement.error.failed"));
    } finally {
      setTranslateLoading(false);
    }
  };

  const handleInsert = () => {
    const promptToInsert = activeTab === 0 ? outputPrompt : imageDescription;
    const outputStr = typeof promptToInsert === "string" ? promptToInsert : String(promptToInsert || "");
    if (outputStr.trim()) {
      onInsert(outputStr);
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // 画像アップロード機能
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];

      // ファイルタイプチェック
      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        setAlert("error", t("juno.input.uploadError.type"));
        return;
      }

      // ファイルサイズチェック (10MB)
      const maxSizeInBytes = 10000000;
      if (file.size > maxSizeInBytes) {
        setAlert("error", t("juno.input.uploadError.size", { size: "10" }));
        return;
      }

      updateImageFromFile(file);
    }
  };

  const handleImageDelete = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleGeneratedImageSelect = (imageUrl: string) => {
    // GeneratedImageModalから渡されるURLはすでにAPIから取得されたBlobのURLなので
    // 直接Fileオブジェクトに変換して使用する
    fetch(imageUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const file = new File([blob], "generated-image.jpg", { type: blob.type });
        updateImageFromFile(file);
      })
      .catch((error) => {
        console.error("Error loading image:", error);
      });
  };

  const updateImageFromFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target && e.target.result) {
        setUploadedImage(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  // タブ変更ハンドラー
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(""); // タブ切り替え時にエラーをクリア
  };

  // 画像分析機能
  const handleAnalyzeImage = async () => {
    if (!uploadedImage) {
      setError(t("juno.enhancement.error.noImage"));
      return;
    }

    setAnalyzeLoading(true);
    setError("");

    try {
      const endpoint = "/api/v1/juno/analyze-image";
      const payload = { image: uploadedImage };

      const response = await apiRequest(endpoint, "POST", payload);

      if (response.data.prompt_en) {
        setImageDescription(response.data.prompt_en);
        setImageDescriptionNative(response.data.prompt_native || "");
        setError(t("juno.enhancement.error.noResponse"));
      }
    } catch (err: any) {
      console.error("Image analysis error:", err);
      setError(err.response?.data?.message || t("juno.enhancement.error.failed"));
    } finally {
      setAnalyzeLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "800px",
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
          backdropFilter: "blur(20px)",
          boxShadow: 24,
          p: 3,
          outline: "none",
          borderRadius: 2,
          maxHeight: "85vh",
          overflow: "auto",
        }}
      >
        {/* Header with Close Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <GradientTypography variant="h6" mb={0.5}>
              {t("juno.enhancement.title")}
            </GradientTypography>
            <Typography variant="body2" color="text.secondary">
              {t("juno.enhancement.description")}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Grid container spacing={3}>
          {/* Tab Navigation */}
          {showImageTab && (
            <Grid size={{ xs: 12 }}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                <Tab label={t("juno.enhancement.tabs.textInput")} />
                <Tab label={t("juno.enhancement.tabs.imageAnalysis")} />
              </Tabs>
            </Grid>
          )}

          {/* Tab Content */}
          {(activeTab === 0 || !showImageTab) && (
            <TextInputTab
              inputPrompt={inputPrompt}
              setInputPrompt={setInputPrompt}
              enhanceLoading={enhanceLoading}
              translateLoading={translateLoading}
              isAnyLoading={isAnyLoading}
              showTranslate={showTranslate}
              onEnhance={handleEnhance}
              onTranslate={handleTranslate}
            />
          )}

          {showImageTab && activeTab === 1 && (
            <>
              <ImageAnalysisTab
                uploadedImage={uploadedImage}
                analyzeLoading={analyzeLoading}
                isAnyLoading={isAnyLoading}
                onImageUpload={handleImageUpload}
                onImageDelete={handleImageDelete}
                onImageClick={handleImageClick}
                onAnalyzeImage={handleAnalyzeImage}
                onGeneratedImageModalOpen={() => setGeneratedImageModalOpen(true)}
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: "none" }}
              />
            </>
          )}

          <OutputSection
            activeTab={activeTab}
            outputPrompt={outputPrompt}
            setOutputPrompt={setOutputPrompt}
            imageDescription={imageDescription}
            setImageDescription={setImageDescription}
            outputPromptNative={outputPromptNative}
            imageDescriptionNative={imageDescriptionNative}
            error={error}
            onInsert={handleInsert}
            onCancel={handleCancel}
          />
        </Grid>

        {/* Generated Image Modal */}
        <GeneratedImageModal
          open={generatedImageModalOpen}
          setOpen={setGeneratedImageModalOpen}
          setImage={handleGeneratedImageSelect}
        />
      </Box>
    </Modal>
  );
};

export default PromptEnhancementModal;
